import React, { useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

import useModalBehavior from '../../hooks/useModalBehavior';

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
  '2xl': 'max-w-6xl',
  '5xl': 'max-w-[80rem]',
  full: 'max-w-[calc(100vw-1rem)] sm:max-w-[calc(100vw-2rem)]',
};

/**
 * Modal compartilhado do Portal do Servidor.
 * Preserva o contrato existente e acrescenta foco, Escape, tamanhos amplos e
 * opção de apresentação mobile sem alterar o conteúdo consumidor.
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEscape = true,
  mobileMode = 'center',
  contentClassName = '',
  panelClassName = '',
}) => {
  const reactId = useId();
  const stableId = reactId.replace(/:/g, '');
  const titleId = `server-modal-${stableId}-title`;
  const descriptionId = `server-modal-${stableId}-description`;
  const canClose = typeof onClose === 'function';
  const dialogRef = useModalBehavior({
    isOpen,
    onClose,
    closeOnEscape: closeOnEscape && canClose,
  });

  if (!isOpen || typeof document === 'undefined') return null;

  const maxWidthClass = sizeClasses[size] || sizeClasses.md;
  const mobilePanelClass = mobileMode === 'sheet'
    ? 'max-sm:mt-auto max-sm:max-h-[92dvh] max-sm:rounded-b-none max-sm:rounded-t-[1.75rem]'
    : mobileMode === 'fullscreen'
      ? 'max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:max-w-none max-sm:rounded-none'
      : '';
  const mobileBackdropClass = mobileMode === 'sheet' ? 'max-sm:items-end max-sm:p-0' : '';

  const handleBackdropMouseDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (closeOnBackdrop && canClose) onClose();
  };

  return createPortal(
    <div
      onMouseDown={handleBackdropMouseDown}
      className={`fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm dark:bg-gray-950/80 sm:p-4 ${mobileBackdropClass}`}
      role="presentation"
    >
      <section
        ref={dialogRef}
        tabIndex={-1}
        className={`flex max-h-[calc(100dvh-1.5rem)] w-full ${maxWidthClass} flex-col overflow-hidden rounded-2xl bg-[var(--sisdp-color-surface)] shadow-2xl ring-1 ring-black/10 dark:ring-white/10 sm:max-h-[calc(100dvh-2rem)] ${mobilePanelClass} ${panelClassName}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? 'Janela de diálogo' : undefined}
        aria-describedby={description ? descriptionId : undefined}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {(title || description || canClose) && (
          <header className="flex shrink-0 items-start justify-between gap-4 border-b border-[var(--sisdp-color-border)] px-4 py-4 sm:px-5">
            <div className="min-w-0">
              {title && (
                <h2 id={titleId} className="text-lg font-black leading-snug text-[var(--sisdp-color-text)]">
                  {title}
                </h2>
              )}
              {description && (
                <p id={descriptionId} className="mt-1 text-sm leading-relaxed text-[var(--sisdp-color-text-muted)]">
                  {description}
                </p>
              )}
            </div>

            {canClose && (
              <button
                type="button"
                onClick={onClose}
                className="server-touch-target -mr-2 inline-flex shrink-0 items-center justify-center rounded-xl text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
                aria-label="Fechar janela"
              >
                <X size={22} aria-hidden="true" />
              </button>
            )}
          </header>
        )}

        <div className={`sisfol-soft-scrollbar flex-1 overflow-y-auto p-4 sm:p-5 ${contentClassName}`}>
          {children}
        </div>

        {footer && (
          <footer className="shrink-0 border-t border-[var(--sisdp-color-border)] bg-[var(--sisdp-color-surface-subtle)] px-4 py-4 sm:px-5">
            {footer}
          </footer>
        )}
      </section>
    </div>,
    document.body
  );
};

export default Modal;
